import React, { useEffect, useState, useRef } from "react";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Cancel as CancelIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Brightness4 as DarkIcon,
  Brightness7 as LightIcon,
  HourglassEmpty as LoadingIcon
} from "@mui/icons-material";
import "./App.css";
import FloatingSocialBar from "./FloatingSocialBar";

const API = {
  getNotes: "https://clipboard-gateway-1wojzp6v.an.gateway.dev/getNotes",
  saveNotes: "https://clipboard-gateway-1wojzp6v.an.gateway.dev/saveNotes"
};

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new window.FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Helper for expiry display
function getExpiryText(expiryTime) {
  if (!expiryTime) return "";
  const now = Date.now();
  const expiry = new Date(expiryTime).getTime();
  const diffSec = Math.round((expiry - now) / 1000);
  if (diffSec > 0) {
    if (diffSec < 60) return `Expires in ${diffSec} seconds`;
    return `Expires in ${Math.floor(diffSec / 60)} minutes`;
  } else {
    return "Can be deleted anytime soon in next 7 minutes";
  }
}

const siteUrl = window.location.origin;

function App() {
  const [tab, setTab] = useState("create");
  const [guid, setGuid] = useState("");
  const [notes, setNotes] = useState(Array(10).fill(null));
  const [editingIdx, setEditingIdx] = useState(null);
  const [editText, setEditText] = useState("");
  const [editImage, setEditImage] = useState(null);
  const fileInputRef = useRef();
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "light");
  const [loading, setLoading] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(false);
  const [guidCopy, setGuidCopy] = useState(false);

  // GET tab state
  const [fetchGuid, setFetchGuid] = useState("");
  const [fetchedNotes, setFetchedNotes] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [noteCopyIdx, setNoteCopyIdx] = useState(null);

  useEffect(() => {
    document.body.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
    // eslint-disable-next-line
  }, [theme]);

  useEffect(() => {
    // Direct code in URL
    const path = window.location.pathname.replace("/", "");
    if (path && path.length === 8 && !guid && !createSuccess) {
      setTab("fetch");
      setFetchGuid(path);
      handleFetch({ preventDefault: () => {} }, path);
    }
  }, []);

  const resetCreateTab = () => {
    setGuid("");
    setNotes(Array(10).fill(null));
    setEditingIdx(null);
    setEditText("");
    setEditImage(null);
    setCreateSuccess(false);
  };

  // Instantly save text/image edits to notes[] in state
  const handleEdit = idx => {
    setEditingIdx(idx);
    if (notes[idx]?.type === "text") {
      setEditText(notes[idx]?.note || "");
      setEditImage(null);
    } else if (notes[idx]?.type === "image") {
      setEditText("");
      setEditImage(notes[idx]?.file || notes[idx]?.note || null);
    } else {
      setEditText("");
      setEditImage(null);
    }
  };

  // Instantly update notes array as user types or uploads
  const handleAutoSave = (idx, text, image) => {
    const updated = [...notes];
    if (image instanceof File) {
      updated[idx] = {
        type: "image",
        note: URL.createObjectURL(image),
        file: image,
        note_index: idx
      };
    } else if (text !== "") {
      updated[idx] = {
        type: "text",
        note: text,
        note_index: idx
      };
    } else {
      updated[idx] = null; // If text cleared, delete note
    }
    setNotes(updated);
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items || [];
    for (let item of items) {
      if (item.type.startsWith("image/")) {
        const file = item.getAsFile();
        setEditImage(file);
        setEditText("");
        handleAutoSave(editingIdx, "", file);
        return;
      }
      if (item.type === "text/plain") {
        item.getAsString(text => {
          setEditText(text);
          handleAutoSave(editingIdx, text, null);
        });
        setEditImage(null);
      }
    }
  };

  const handleImageUpload = (e, idx) => {
    const file = e.target.files?.[0];
    if (file) {
      setEditImage(file);
      setEditText("");
      handleAutoSave(idx, "", file);
    }
  };

  // Remove local note (no API)
  const handleDeleteLocal = idx => {
    const updated = [...notes];
    updated[idx] = null;
    setNotes(updated);
    if (editingIdx === idx) {
      setEditingIdx(null);
      setEditText("");
      setEditImage(null);
    }
  };

  // Called on "Save All & Get Unique Code"
  const handleSubmitAll = async () => {
    setLoading(true);
    try {
      // Prepare all note payloads (convert image files to base64)
      const preparedNotes = await Promise.all(
        notes.map(async (n, idx) => {
          if (!n) return null;
          if (n.type === "image" && n.file) {
            return {
              note_index: idx,
              type: "image",
              note: await fileToBase64(n.file),
            };
          } else if (n.type === "text") {
            return {
              note_index: idx,
              type: "text",
              note: n.note,
            };
          }
          return null;
        })
      );
      const toSend = preparedNotes.filter(Boolean);

      const response = await fetch(API.saveNotes, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          notes: toSend
        })
      });
      const data = await response.json();
      setGuid(data.guid);
      setCreateSuccess(true);
    } catch (err) {
      alert("Error saving notes. Please try again.");
    }
    setLoading(false);
  };

  // GET tab
  const handleFetch = async (e, guid=null) => {
    if (e && e.preventDefault) e.preventDefault();
    setFetchError("");
    setFetchedNotes(null);
    setFetchLoading(true);
    try {
      const res = await fetch(`${API.getNotes}?guid=${guid ? guid : fetchGuid.trim()}`);
      if (!res.ok) throw new Error("No notes found for this Unique Code.");
      const data = await res.json();
      if (!data.length) throw new Error("No notes found for this Unique Code.");
      const notesArr = Array(10).fill(null);
      data.forEach(n => notesArr[n.note_index] = n);
      setFetchedNotes(notesArr);
    } catch (err) {
      setFetchError("No notes found for this Unique Code.");
    }
    setFetchLoading(false);
  };

  // Copy unique code or URL to clipboard
  const handleCopyGuid = () => {
    navigator.clipboard.writeText(guid);
    setGuidCopy(true);
    setTimeout(() => setGuidCopy(false), 500);
  };

  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url);
    setGuidCopy(true);
    setTimeout(() => setGuidCopy(false), 500);
  };

  // Copy note text to clipboard or download image
  const handleCopyNote = (note, idx) => {
    if (note.type === "text") {
      navigator.clipboard.writeText(note.note);
      setNoteCopyIdx(idx);
      setTimeout(() => setNoteCopyIdx(null), 1200);
    }
  };

  const downloadImage = (url) => {
  fetch(url)
    .then(response => response.blob())
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      // a.download = 'image.png'; 
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    });
}

  const handleDownloadImage = (note) => {
    if (note.type === "image") {
      downloadImage(note.note);
    }
  };

  // UI
  return (
    <div className="main-container">
      {loading &&
        <div className="loading-overlay">
          <LoadingIcon className="loading-icon" style={{ fontSize: 52, color: "#1976d2" }} />
          <div className="loading-msg">Saving your notes...</div>
        </div>
      }
      <header className="header">
        <span className="header-title"><img src={`${process.env.PUBLIC_URL}/title.png`} height="150px" alt="title" /></span>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <button
            className={`tab-btn${tab === "create" ? " active" : ""}`}
            onClick={() => { setTab("create"); resetCreateTab(); }}
            disabled={tab === "create"}
          >Create Notes</button>
          <button
            className={`tab-btn${tab === "fetch" ? " active" : ""}`}
            onClick={() => { setTab("fetch"); setFetchedNotes(null); setFetchGuid(""); setFetchError(""); }}
            disabled={tab === "fetch"}
          >Get Notes</button>
          <button className="theme-btn" onClick={() => setTheme(theme === "light" ? "dark" : "light")}>
            {theme === "light" ? <DarkIcon /> : <LightIcon />}
            <span style={{ marginLeft: 8 }}>
              {theme === "light" ? "Dark" : "Light"}
            </span>
          </button>
        </div>
      </header>

      <div className="welcome-info" style={{ margin: "0 0 1.6em 0" }}>
        <div style={{ fontSize: 21, fontWeight: 600, marginBottom: 6 }}>
          Welcome to Online Clipboard!
        </div>
        <div style={{ fontSize: 16, marginBottom: 8 }}>
          Securely save up to <b style={{ color: "#1976d2" }}>10 notes</b> (text or images) for <b style={{ color: "#e57373" }}>~30 minutes expiry time</b> with a unique shareable link.<br />
          No signup or personal data required. Share your notes across devices or with others using the generated <b>Unique Code</b>.<br />
          <b>Precautions:</b> Avoid storing confidential info, your data is auto-deleted after <span style={{ color: "#e57373" }}>~30 minutes</span>.
        </div>
      </div>

      {/* CREATE TAB */}
      {tab === "create" && (
        <>
          {!createSuccess &&
            <button
              className="note-btn primary"
              style={{ margin: "32px auto 0 auto", display: "block", fontSize: 18, padding: "0.7em 2.3em" }}
              onClick={handleSubmitAll}
              disabled={loading || notes.every(n => !n)}
            >
              Save All & Get Unique Code
            </button>
          }
          {createSuccess && <div style={{ margin: "1em 0 1.7em 0", fontSize: 16 }}>
            <b>Your Clipboard Unique Code:</b>
            <span className="guid-badge">{guid}</span>
            <button className="copy-guid-btn" onClick={() => handleCopyGuid(`${siteUrl}/${guid}`)} title="Copy Code">
              <CopyIcon fontSize="small" />
            </button>
            {guidCopy && <span className="copied-label">Copied!</span>}
            <br />
            <b>Share URL:</b>
            <span className="guid-badge">{`${siteUrl}/${guid}`}</span>
            <button className="copy-guid-btn" onClick={() => handleCopyUrl(`${siteUrl}/${guid}`)} title="Copy URL">
              <CopyIcon fontSize="small" />
            </button>
            {guidCopy && <span className="copied-label">Copied!</span>}
          </div>}
          {createSuccess &&
            <div style={{ background: "#d1ffd6", color: "#225c22", padding: "12px 18px", borderRadius: 8, marginBottom: 16, fontWeight: 500 }}>
              Notes created! Copy your Unique Code or URL to use it on any device or share with others.<br />
              <button className="note-btn" style={{ marginTop: 8 }} onClick={resetCreateTab}>Start new clipboard</button>
            </div>
          }
          <div className="notes-grid">
            {notes.map((note, idx) => (
              <div
                key={idx}
                className={`note-card ${editingIdx === idx ? "editing" : ""} ${!note ? "note-card-empty" : ""}`}
                onClick={() => {
                  if (!note && editingIdx !== idx && !createSuccess && !loading) handleEdit(idx);
                }}
                style={{
                  cursor: !note && editingIdx !== idx && !createSuccess && !loading ? "pointer" : "default",
                }}
              >
                <div className="note-card-title">Note {idx + 1}</div>
                {editingIdx === idx && !createSuccess && !loading ? (
                  <>
                    {!editImage && <textarea
                      className="note-textarea"
                      value={editText}
                      onChange={e => {
                        setEditText(e.target.value);
                        handleAutoSave(idx, e.target.value, null);
                      }}
                      onPaste={handlePaste}
                      disabled={!!editImage}
                      placeholder="Paste or write text, or upload an image"
                      autoFocus
                    />}
                    {editImage && (
                      <div className="note-image-preview">
                        <img
                          src={editImage instanceof File ? URL.createObjectURL(editImage) : editImage}
                          alt="preview"
                        />
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileInputRef}
                      style={{ display: "none" }}
                      onChange={e => handleImageUpload(e, idx)}
                    />
                    <div className="note-actions">
                      <button
                        className="note-btn"
                        onClick={e => { e.stopPropagation(); fileInputRef.current.click(); }}
                        disabled={loading}
                      >
                        <ImageIcon />
                        Upload Image
                      </button>
                      <button
                        className="note-btn"
                        onClick={e => {
                          e.stopPropagation();
                          setEditingIdx(null);
                          setEditText("");
                          setEditImage(null);
                        }}
                        disabled={loading}
                      >
                        <CancelIcon />
                        Cancel
                      </button>
                      {/* No Done button: auto-persist */}
                    </div>
                  </>
                ) : note ? (
                  <>
                    {note.type === "text" && (
                      <div className="note-content">{note.note}</div>
                    )}
                    {note.type === "image" && (
                      <div className="note-image">
                        <img src={note.note} alt="clip" />
                      </div>
                    )}
                    {!createSuccess && !loading && (
                      <div className="note-actions">
                        <button
                          className="note-btn"
                          onClick={e => { e.stopPropagation(); handleEdit(idx); }}
                          disabled={loading}
                        >
                          <EditIcon />
                          Edit
                        </button>
                        <button
                          className="note-btn danger"
                          onClick={e => { e.stopPropagation(); handleDeleteLocal(idx); }}
                          disabled={loading}
                        >
                          <DeleteIcon />
                          Delete
                        </button>
                      </div>
                    )}
                    {/* Always show Delete for non-empty notes */}
                    {/* {!createSuccess && !loading && (
                      <div className="note-actions">
                        <button
                          className="note-btn danger"
                          onClick={e => { e.stopPropagation(); handleDeleteLocal(idx); }}
                          disabled={loading}
                        >
                          <DeleteIcon />
                          Delete
                        </button>
                      </div>
                    )} */}
                  </>
                ) : (
                  <div className="note-empty">{createSuccess ? "[Empty Slot]" : "[Click to add a note]"}</div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* FETCH TAB */}
      {tab === "fetch" && (
        <div style={{ margin: "2.2em auto", maxWidth: 580 }}>
          <form onSubmit={handleFetch} style={{ display: "flex", gap: 16, alignItems: "center" }}>
            <input
              type="text"
              value={fetchGuid}
              onChange={e => setFetchGuid(e.target.value)}
              placeholder="Enter Unique Code to fetch notes"
              className="fetch-guid-input"
              required
              style={{ flex: 1, fontSize: 18, padding: "0.5em 1.1em", borderRadius: 7, border: "1.7px solid #bbb" }}
            />
            <button className="note-btn primary" type="submit" disabled={fetchLoading || !fetchGuid}>
              Get Notes
            </button>
          </form>
          {fetchLoading && <div className="loading-indicator">Loading notes…</div>}
          {fetchError && <div style={{ color: "#d32f2f", fontWeight: 500, marginTop: 18 }}>{fetchError}</div>}
          {fetchedNotes &&
            <div className="notes-grid" style={{ marginTop: 36 }}>
              {fetchedNotes.map((note, idx) => (
                <div
                  key={idx}
                  className={`note-card read-only ${!note ? "note-card-empty" : ""}`}
                  style={{ cursor: "default", boxShadow: "0 1px 10px 0 rgba(50,50,50,0.04)" }}
                >
                  <div className="note-card-title">Note {idx + 1} 
                      {note && <>
                      
                      {note.type === "text" 
                      ? <>
                      <button className="copy-note-btn"
                            onClick={() => handleCopyNote(note, idx)}
                            title="Copy text to clipboard"
                            style={{ marginLeft: 8 }}
                            disabled={!note}
                          >
                            <CopyIcon fontSize="small" />
                          </button>
                          {(noteCopyIdx === idx) && <span className="copied-label">Copied!</span>}
                          </>
                      : <><a href={note.note} target="_blank" rel="noopener noreferrer" download>
                          <button
                            className="copy-note-btn"
                            // onClick={() => handleDownloadImage(note)}
                            title="Download image"
                          >
                            <DownloadIcon fontSize="small" />
                          </button>
                          </a></>
                          }
                          </>
                      }
                      </div>
                  {note ? (
                    <>
                      {note.type === "text" && (
                        <div className="note-content">
                          {note.note}
                        </div>
                      )}
                      {note.type === "image" && (
                        <div className="note-image">
                          <img src={note.note} alt="clip" />
                        </div>
                      )}
                      <div className="note-meta">
                        {note.expiry_time ? getExpiryText(note.expiry_time) : ""}
                      </div>
                    </>
                  ) : (
                    <div className="note-empty">[Empty Slot]</div>
                  )}
                </div>
              ))}
            </div>
          }
        </div>
      )}
      <FloatingSocialBar />
      <footer className="footer" style={{ marginTop: 42 }}>
        <span>
          &copy; {new Date().getFullYear()} Online Clipboard. All rights reserved.{" "}
        </span>
      </footer>
    </div>
  );
}

export default App;
